// Mapa de categorias do Mercado Livre (plano canal-ml, F7).
// Lista as categorias internas com quantos produtos prontos (ativo no Sisplan + foto do canal) cada uma
// tem, deixa pedir sugestao ao ML (domain_discovery), completar genero, faixa etaria, tipo de medida,
// colunas do guia -> medida do ML e pacote padrao, e grava em config.ml_category_map via
// PATCH /api/channels/:slug (com audit). Mostra quantos produtos prontos ficam sem categoria.
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import {
  AGE_GROUPS,
  GENDERS,
  LISTING_TYPES,
  MEASURE_ATTR_SUGGESTIONS,
  MEASURE_TYPES,
  PKG_LABEL,
  applyCategoryEdit,
  entryToDraft,
  fixedValueOf,
  withFixed,
  suggestFiltrableAttr,
  unmappedText,
  validateDraft,
  type CategoryDraft,
  type CategoryEntry,
} from "../../lib/ml-channel";
import { MlSizeAliasesSection } from "./MlSizeAliasesSection";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select } from "../ui/select";
import { Skeleton } from "../ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../ui/dialog";

interface CategoryRow {
  id: number;
  parent_id: number | null;
  name: string;
  sku_count: number;
  guide: { title: string; columns: string[] } | null;
  entry: CategoryEntry | null;
  /** Ausentes em API antiga. */
  products?: Array<{ sku: string; name: string }>;
  products_total?: number;
  mixed_types?: boolean;
}
interface CategoryAttributesResponse {
  category_id: string;
  needs_size_chart: boolean;
  attributes: Array<{
    id: string;
    name: string;
    required: boolean;
    conditional: boolean;
    auto: boolean;
    values: Array<{ id: string; name: string }>;
    value_type: string;
  }>;
}
interface CategoryMapResponse {
  slug: string;
  map: Record<string, CategoryEntry>;
  categories: CategoryRow[];
  skus_total: number;
  skus_unmapped: number;
  skus_unmapped_sample: string[];
  /** Ausentes em API antiga: tratar como vazio. */
  size_aliases?: Record<string, string>;
  sizes_in_use?: string[];
}
interface Suggestion {
  domain_id: string;
  domain_name: string;
  category_id: string;
  category_name: string;
}

export function MlCategoryMapDialog({
  slug,
  name,
  open,
  onOpenChange,
}: {
  slug: string;
  name: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Mapa de categorias — {name}</DialogTitle>
          <DialogDescription>
            Cada categoria do Vitrine precisa apontar para uma categoria do Mercado Livre. Produto sem categoria mapeada não é publicado.
          </DialogDescription>
        </DialogHeader>
        <MlCategoryMapPanel slug={slug} />
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">Fechar</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Corpo do mapa de categorias: usado no dialogo (lista de canais) e na aba "Regras do Mercado Livre". */
export function MlCategoryMapPanel({ slug }: { slug: string }): JSX.Element {
  const [selected, setSelected] = useState<number | null>(null);
  const q = useQuery<CategoryMapResponse>({
    queryKey: ["ml-category-map", slug],
    queryFn: async () => (await api.get<CategoryMapResponse>(`/api/channels/${slug}/ml/category-map`)).data,
  });
  const cat = q.data?.categories.find((c) => c.id === selected) ?? null;

  if (q.isLoading) return <Skeleton className="h-40 w-full" />;
  if (q.isError || !q.data) {
    return (
      <p className="field__hint" style={{ color: "var(--color-warn)" }}>
        Não deu para ler as categorias ({apiErrorMessage(q.error)}).
      </p>
    );
  }
  return (
    <div className="stack" style={{ gap: "var(--sp-3)" }}>
      <p
        className="text-md"
        role="status"
        style={{ fontWeight: 700, color: q.data.skus_unmapped > 0 ? "var(--color-warn)" : "var(--color-ink)" }}
      >
        {unmappedText(q.data.skus_unmapped, q.data.skus_total)}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-[16rem_1fr] gap-4" style={{ alignItems: "start" }}>
        <ul className="stack" style={{ gap: ".25rem", margin: 0, padding: 0, listStyle: "none" }} aria-label="Categorias do Vitrine">
          {q.data.categories.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => setSelected(c.id)}
                aria-pressed={selected === c.id}
                className="spread"
                style={{
                  width: "100%",
                  textAlign: "left",
                  minHeight: "2.5rem",
                  padding: ".375rem .625rem",
                  paddingLeft: c.parent_id != null ? "1.5rem" : ".625rem",
                  borderRadius: "var(--radius-md)",
                  border: ".0625rem solid var(--color-line)",
                  background: selected === c.id ? "var(--color-soft)" : "transparent",
                  cursor: "pointer",
                }}
              >
                <span className="truncate" style={{ fontWeight: c.parent_id == null ? 800 : 600 }}>{c.name}</span>
                <span className="cluster" style={{ flexShrink: 0 }}>
                  <span className="field__hint">{c.sku_count}</span>
                  {c.entry?.category_id ? <Badge variant="success">mapeada</Badge> : c.sku_count > 0 ? <Badge variant="warning">sem ML</Badge> : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
        {cat ? (
          <CategoryEditor key={cat.id} slug={slug} cat={cat} currentMap={q.data.map} />
        ) : (
          <p className="field__hint">Escolha uma categoria à esquerda para mapear.</p>
        )}
      </div>
      <MlSizeAliasesSection
        key={JSON.stringify(q.data.size_aliases ?? {})}
        slug={slug}
        saved={q.data.size_aliases ?? {}}
        sizesInUse={q.data.sizes_in_use ?? []}
      />
    </div>
  );
}

function CategoryEditor({
  slug,
  cat,
  currentMap,
}: {
  slug: string;
  cat: CategoryRow;
  currentMap: Record<string, CategoryEntry>;
}): JSX.Element {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<CategoryDraft>(() => entryToDraft(cat.entry));
  const [searchText, setSearchText] = useState("");
  const set = <K extends keyof CategoryDraft>(k: K, v: CategoryDraft[K]): void => setDraft((d) => ({ ...d, [k]: v }));
  const errors = useMemo(() => validateDraft(draft), [draft]);
  const hasErrors = Object.keys(errors).length > 0;
  const mapped = !!cat.entry?.category_id;
  const mlCategory = draft.category_id.trim();
  const attrsQ = useQuery<CategoryAttributesResponse>({
    queryKey: ["ml-category-attributes", slug, mlCategory, draft.domain_id.trim()],
    queryFn: async () =>
      (await api.get<CategoryAttributesResponse>(`/api/channels/${slug}/ml/category-attributes`, { params: { category_id: mlCategory, ...(draft.domain_id.trim() ? { domain_id: draft.domain_id.trim() } : {}) } })).data,
    enabled: /^MLB\d+$/.test(mlCategory),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
  const fixable = (attrsQ.data?.attributes ?? []).filter((a) => !a.auto);

  const suggest = useMutation({
    mutationFn: async () =>
      (
        await api.get<{ q: string; suggestions: Suggestion[] }>(`/api/channels/${slug}/ml/category-suggestions`, {
          params: { category_id: cat.id, ...(searchText.trim() ? { q: searchText.trim() } : {}) },
        })
      ).data,
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const save = useMutation({
    // Manda SO esta categoria: o servidor aplica sobre o mapa que esta no banco, entao duas pessoas editando
    // categorias diferentes nao se sobrescrevem.
    mutationFn: async (next: Record<string, CategoryEntry>) =>
      (await api.patch(`/api/channels/${slug}/ml/rules`, { config: { ml_category_map_patch: { [String(cat.id)]: next[String(cat.id)] ?? null } } })).data,
    onSuccess: () => {
      toast.success("Mapa de categorias salvo");
      void qc.invalidateQueries({ queryKey: ["ml-category-map", slug] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const applySuggestion = (s: Suggestion): void => {
    setDraft((d) => ({
      ...d,
      category_id: s.category_id,
      domain_id: s.domain_id,
      filtrable_size_attr: d.filtrable_size_attr || suggestFiltrableAttr(s.domain_id),
    }));
  };

  return (
    <form
      className="stack"
      style={{ gap: "var(--sp-3)", minWidth: 0 }}
      onSubmit={(e) => {
        e.preventDefault();
        if (!hasErrors) save.mutate(applyCategoryEdit(currentMap, cat.id, draft));
      }}
    >
      <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 900 }}>
        {cat.name} <span className="field__hint">({cat.sku_count} produtos prontos)</span>
      </h3>

      {mapped && (cat.products_total ?? 0) > 0 ? (
        <details>
          <summary style={{ cursor: "pointer", fontWeight: 700 }}>
            Produtos que caem nesta categoria do ML ({cat.products_total})
          </summary>
          {cat.mixed_types ? (
            <p className="field__hint" role="status" style={{ color: "var(--color-warn)" }}>
              Atenção: esta categoria cobre produtos de tipos diferentes (por exemplo {(cat.products ?? []).slice(0, 3).map((p) => p.name).join(", ")}).
              Crie uma subcategoria para cada tipo em Categorias e mapeie cada uma no Mercado Livre.
            </p>
          ) : null}
          <ul className="stack" style={{ gap: ".125rem", margin: ".375rem 0 0", padding: 0, listStyle: "none", maxHeight: "12rem", overflowY: "auto" }} aria-label="Produtos desta categoria">
            {(cat.products ?? []).map((p) => (
              <li key={p.sku} className="cluster" style={{ gap: ".5rem" }}>
                <span className="mono field__hint">{p.sku}</span>
                <span className="truncate">{p.name}</span>
              </li>
            ))}
          </ul>
          {(cat.products_total ?? 0) > (cat.products ?? []).length ? (
            <p className="field__hint">Mostrando os primeiros {(cat.products ?? []).length} de {cat.products_total}.</p>
          ) : null}
        </details>
      ) : null}

      <div className="stack" style={{ gap: ".5rem" }}>
        <div className="field">
          <Label htmlFor="mlc-q">Buscar no Mercado Livre</Label>
          <div className="cluster">
            <Input id="mlc-q" value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder={cat.name} className="flex-1" />
            <Button type="button" size="sm" variant="secondary" onClick={() => suggest.mutate()} loading={suggest.isPending}>
              <Sparkles className="icon" aria-hidden /> Sugerir
            </Button>
          </div>
        </div>
        {suggest.data ? (
          suggest.data.suggestions.length === 0 ? (
            <p className="field__hint">O Mercado Livre não sugeriu nada para "{suggest.data.q}". Tente outro texto.</p>
          ) : (
            <ul className="stack" style={{ gap: ".25rem", margin: 0, padding: 0, listStyle: "none" }} aria-label="Sugestões do Mercado Livre">
              {suggest.data.suggestions.map((s) => (
                <li key={s.category_id} className="spread" style={{ border: ".0625rem solid var(--color-line)", borderRadius: "var(--radius-md)", padding: ".375rem .625rem" }}>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ fontWeight: 700 }}>{s.category_name}</span>{" "}
                    <span className="field__hint mono">{s.category_id} · {s.domain_id}</span>
                  </span>
                  <Button type="button" size="sm" variant="outline" onClick={() => applySuggestion(s)} style={{ flexShrink: 0 }}>Usar</Button>
                </li>
              ))}
            </ul>
          )
        ) : null}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="field">
          <Label htmlFor="mlc-cat">Categoria do ML</Label>
          <Input id="mlc-cat" value={draft.category_id} onChange={(e) => set("category_id", e.target.value.toUpperCase())} placeholder="MLB108716" className="mono" aria-invalid={!!errors.category_id} />
          {errors.category_id ? <p className="field__hint" style={{ color: "var(--color-bad)" }}>{errors.category_id}</p> : null}
        </div>
        <div className="field">
          <Label htmlFor="mlc-dom">Domínio</Label>
          <Input id="mlc-dom" value={draft.domain_id} onChange={(e) => set("domain_id", e.target.value.toUpperCase())} placeholder="MLB-T_SHIRTS" className="mono" aria-invalid={!!errors.domain_id} />
          {errors.domain_id ? <p className="field__hint" style={{ color: "var(--color-bad)" }}>{errors.domain_id}</p> : null}
        </div>
        <div className="field">
          <Label htmlFor="mlc-gen">Gênero</Label>
          <Select id="mlc-gen" value={draft.ml_gender} onChange={(e) => set("ml_gender", e.target.value)}>
            <option value="">(padrão)</option>
            {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
          </Select>
        </div>
        <div className="field">
          <Label htmlFor="mlc-age">Faixa etária</Label>
          <Select id="mlc-age" value={draft.ml_age_group} onChange={(e) => set("ml_age_group", e.target.value)}>
            <option value="">(padrão)</option>
            {AGE_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
          </Select>
        </div>
        <div className="field">
          <Label htmlFor="mlc-list">Tipo de anúncio</Label>
          <Select id="mlc-list" value={draft.ml_listing_type} onChange={(e) => set("ml_listing_type", e.target.value)}>
            <option value="">(padrão do canal)</option>
            {LISTING_TYPES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </Select>
        </div>
        <div className="field">
          <Label htmlFor="mlc-model">Modelo</Label>
          <Input id="mlc-model" value={draft.ml_model} onChange={(e) => set("ml_model", e.target.value)} />
        </div>
        <div className="field sm:col-span-2">
          <Label htmlFor="mlc-piece">Tipo da peça (título)</Label>
          <Input id="mlc-piece" value={draft.ml_piece_type} onChange={(e) => set("ml_piece_type", e.target.value)} placeholder="Short Fitness Feminino" />
          <p className="field__hint">Usado quando o nome do produto tem menos de 3 palavras: Tipo + Hiit + nome. Ex.: Short Fitness Feminino Hiit Team</p>
        </div>
      </div>

      <fieldset className="stack" style={{ gap: ".5rem", border: ".0625rem solid var(--color-line)", borderRadius: "var(--radius-md)", padding: ".625rem" }}>
        <legend style={{ fontWeight: 800, padding: "0 .375rem" }}>Embalagem padrão da categoria</legend>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {(["ml_pkg_height_cm", "ml_pkg_width_cm", "ml_pkg_length_cm", "ml_pkg_weight_g"] as const).map((k) => (
            <div className="field" key={k}>
              <Label htmlFor={`mlc-${k}`}>{PKG_LABEL[k]}</Label>
              <Input
                id={`mlc-${k}`}
                type="number"
                inputMode="numeric"
                min={1}
                step={1}
                value={draft[k]}
                onChange={(e) => set(k, e.target.value)}
                aria-invalid={!!errors[k]}
              />
              {errors[k] ? <p className="field__hint" style={{ color: "var(--color-bad)" }}>{errors[k]}</p> : null}
            </div>
          ))}
        </div>
        <p className="field__hint">O produto pode ter embalagem própria na ficha dele; esta é a de quando ele não tem.</p>
      </fieldset>

      <fieldset className="stack" style={{ gap: ".5rem", border: ".0625rem solid var(--color-line)", borderRadius: "var(--radius-md)", padding: ".625rem" }}>
        <legend style={{ fontWeight: 800, padding: "0 .375rem" }}>Tabela de medidas</legend>
        <label className="cluster" style={{ gap: ".5rem" }}>
          <input type="checkbox" checked={draft.size_chart} onChange={(e) => set("size_chart", e.target.checked)} />
          Esta categoria usa tabela de medidas no Mercado Livre
        </label>
        {attrsQ.data && attrsQ.data.needs_size_chart && !draft.size_chart ? (
          <p className="field__hint" role="status" style={{ color: "var(--color-warn)" }}>
            O Mercado Livre exige tabela de medidas nesta categoria. Com a tabela desligada o anúncio é recusado: marque a opção acima e ligue um guia de tamanhos.
          </p>
        ) : null}
        {attrsQ.data && !attrsQ.data.needs_size_chart && draft.size_chart ? (
          <p className="field__hint">O Mercado Livre não exige tabela de medidas nesta categoria; ela só é enviada se você deixar ligada.</p>
        ) : null}
        {draft.size_chart ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="field">
                <Label htmlFor="mlc-mt">Tipo de medida</Label>
                <Select id="mlc-mt" value={draft.measure_type} onChange={(e) => set("measure_type", e.target.value)}>
                  {MEASURE_TYPES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </Select>
              </div>
              <div className="field">
                <Label htmlFor="mlc-fa">Atributo do tamanho filtrável</Label>
                <Input id="mlc-fa" value={draft.filtrable_size_attr} onChange={(e) => set("filtrable_size_attr", e.target.value.toUpperCase())} placeholder={suggestFiltrableAttr(draft.domain_id) || "T_SHIRTS_FILTRABLE_SIZES"} className="mono" />
              </div>
            </div>
            {cat.guide ? (
              <div className="stack" style={{ gap: ".375rem" }}>
                <div className="field__hint">
                  Guia de tamanhos "{cat.guide.title}": diga qual medida do ML corresponde a cada coluna. Deixe vazio o que o ML não usa (ex.: a coluna do nome do tamanho).
                </div>
                <datalist id="mlc-attrs">
                  {MEASURE_ATTR_SUGGESTIONS.map((a) => <option key={a} value={a} />)}
                </datalist>
                {cat.guide.columns.map((col) => (
                  <div className="field" key={col}>
                    <Label htmlFor={`mlc-col-${col}`}>Coluna "{col}"</Label>
                    <Input
                      id={`mlc-col-${col}`}
                      list="mlc-attrs"
                      value={draft.columns_map[col] ?? ""}
                      onChange={(e) => set("columns_map", { ...draft.columns_map, [col]: e.target.value.toUpperCase() })}
                      placeholder="WAIST_CIRCUMFERENCE_FROM"
                      className="mono"
                      aria-invalid={!!errors[`col:${col}`]}
                    />
                    {errors[`col:${col}`] ? <p className="field__hint" style={{ color: "var(--color-bad)" }}>{errors[`col:${col}`]}</p> : null}
                  </div>
                ))}
              </div>
            ) : (
              <p className="field__hint" style={{ color: "var(--color-warn)" }}>
                Esta categoria não tem guia de tamanhos no Vitrine. Cadastre o guia em Guias de tamanho e volte aqui para ligar as colunas.
              </p>
            )}
          </>
        ) : null}
      </fieldset>

      <fieldset className="stack" style={{ gap: ".5rem", border: ".0625rem solid var(--color-line)", borderRadius: "var(--radius-md)", padding: ".625rem" }}>
        <legend style={{ fontWeight: 800, padding: "0 .375rem" }}>Ficha técnica da categoria</legend>
        <p className="field__hint" style={{ margin: 0 }}>
          Valores que valem para todo produto desta categoria quando o produto não tem o dado (ex.: Tipo de roupa = Regata). O que o produto já carrega vence.
        </p>
        {!/^MLB\d+$/.test(mlCategory) ? (
          <p className="field__hint">Informe a categoria do ML acima para ver os atributos dela.</p>
        ) : attrsQ.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : attrsQ.isError || !attrsQ.data ? (
          <p className="field__hint" style={{ color: "var(--color-warn)" }}>Não deu para ler os atributos da categoria no Mercado Livre ({apiErrorMessage(attrsQ.error)}).</p>
        ) : fixable.length === 0 ? (
          <p className="field__hint">Esta categoria não tem atributos para fixar.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fixable.map((a) => {
              const closed = a.values.length > 0;
              const current = fixedValueOf(draft.fixed_attributes, a.id);
              return (
                <div className="field" key={a.id}>
                  <Label htmlFor={`mlc-fx-${a.id}`}>
                    {a.name} {a.required ? <Badge variant="warning">obrigatório</Badge> : a.conditional ? <Badge variant="draft">às vezes obrigatório</Badge> : null}
                  </Label>
                  {closed ? (
                    <Select id={`mlc-fx-${a.id}`} value={current} onChange={(e) => set("fixed_attributes", withFixed(draft.fixed_attributes, a.id, e.target.value, true))}>
                      <option value="">(vem do produto)</option>
                      {a.values.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </Select>
                  ) : (
                    <Input id={`mlc-fx-${a.id}`} value={current} onChange={(e) => set("fixed_attributes", withFixed(draft.fixed_attributes, a.id, e.target.value, false))} placeholder="(vem do produto)" />
                  )}
                </div>
              );
            })}
          </div>
        )}
        {draft.fixed_attributes.some((f) => !fixable.some((a) => a.id === f.id)) && attrsQ.data ? (
          <p className="field__hint">
            Também há valores fixos guardados para atributos que o ML não lista mais ({draft.fixed_attributes.filter((f) => !fixable.some((a) => a.id === f.id)).map((f) => f.id).join(", ")}); eles continuam valendo.
          </p>
        ) : null}
      </fieldset>

      <div className="spread">
        {mapped ? (
          <Button type="button" variant="outline" disabled={save.isPending} onClick={() => save.mutate(applyCategoryEdit(currentMap, cat.id, null))}>
            Remover mapeamento
          </Button>
        ) : <span />}
        <Button type="submit" disabled={hasErrors || save.isPending} loading={save.isPending}>Salvar categoria</Button>
      </div>
      <p className="field__hint">
        Salvar não republica nada sozinho; os produtos pegam o mapa novo na próxima publicação ou republicação.
      </p>
    </form>
  );
}
